import { useLayoutEffect, useMemo, useRef } from 'react';
import {
  DataFilterDefinitionsDtoResponse,
  DataTableDataSearchDto,
  NavigationCustomizationItemDto,
} from '@asset-metrix/portal-gateway-client-js';
import { UseQueryResult } from '@tanstack/react-query';
import deepEqual from 'deep-equal';

import { useStore } from '../../store';
import { FilterSelections } from '../../store/states/filterSelection';
import {
  FilterConfigDto,
  FilterData,
  isDateRangeFilter,
  isDateRangePickerDisplay,
  isMultiSelectFilter,
  isNumberRangeFilter,
  isSearchStringFilter,
  isSingleSelectFilter,
} from '../../store/states/filterTypes';
import { PermanentError } from '../../utils/errors';
import { getDetailsFromSearchParams } from '../../utils/getDetailsFromSearchParams';
import { queryClient } from '../../utils/queryClient';

export type FilterQueryResult =
  UseQueryResult<DataFilterDefinitionsDtoResponse>;

export type Selection = DataTableDataSearchDto['selection'];

export const EMPTY_FILTER_CONFIGS: FilterConfigDto[] = [];

function extractSelectionsFromResponse(
  data: DataFilterDefinitionsDtoResponse,
  filterConfigs: FilterConfigDto[]
): FilterSelections {
  const selections: FilterSelections = {};
  for (const config of filterConfigs) {
    const filterData = data.data[config.key];
    if (
      filterData != null &&
      filterData.type !== 'error' &&
      filterData.selection != null
    ) {
      selections[config.key] = filterData.selection;
    }
  }
  return selections;
}

// This cache uses WeakRef to avoid memory leaks.
// Unreferenced data is garbage collected, this is the lesser of two evils.
// It might lead to stale data during development, if we encounter issues, let's rework it.
const lastSanitizedData = new Map<
  string,
  WeakRef<DataFilterDefinitionsDtoResponse>
>();

export function useSanitizeSelections(
  query: FilterQueryResult,
  entityId: string,
  filterConfigs: FilterConfigDto[],
  currentSelections: FilterSelections,
  buildQueryKey: (sanitizedSelections: Selection) => readonly unknown[]
): boolean {
  const { sanitized, selectionsMatch } = useMemo(() => {
    if (filterConfigs.length === 0 || query.data == null || query.isFetching) {
      return { sanitized: undefined, selectionsMatch: true };
    }
    const sanitized = extractSelectionsFromResponse(query.data, filterConfigs);
    const currentManagedSelections: FilterSelections = {};
    for (const config of filterConfigs) {
      if (config.key in currentSelections) {
        currentManagedSelections[config.key] = currentSelections[config.key];
      }
    }
    return {
      sanitized,
      selectionsMatch: deepEqual(sanitized, currentManagedSelections),
    };
  }, [query.data, query.isFetching, filterConfigs, currentSelections]);

  const isSanitized = !query.isSuccess || selectionsMatch;

  const buildQueryKeyRef = useRef(buildQueryKey);
  buildQueryKeyRef.current = buildQueryKey;

  useLayoutEffect(() => {
    if (sanitized == null || query.data == null) {
      return;
    }

    const cacheKey = `${entityId}::${filterConfigs.map((c) => c.key).join(',')}`;

    if (
      query.data === lastSanitizedData.get(cacheKey)?.deref() &&
      selectionsMatch
    ) {
      return;
    }
    lastSanitizedData.set(cacheKey, new WeakRef(query.data));

    if (!selectionsMatch) {
      const state = useStore.getState();
      state.resetPagination(entityId);

      const keysToRemove = filterConfigs
        .filter(
          (config) =>
            !(config.key in sanitized) && config.key in currentSelections
        )
        .map((config) => config.key);
      state.batchPresetFilterSelections(entityId, sanitized, keysToRemove);

      queryClient.setQueryData(buildQueryKeyRef.current(sanitized), query.data);
    }
  }, [
    sanitized,
    selectionsMatch,
    query.data,
    entityId,
    filterConfigs,
    currentSelections,
  ]);

  return isSanitized;
}

export function validateSingleFilterFromResponse(
  filterConfig: FilterConfigDto,
  filterData: DataFilterDefinitionsDtoResponse['data'][string],
  activeDynamicNavigationItem?: NavigationCustomizationItemDto
): FilterData {
  if (filterData == null) {
    throw new PermanentError(
      `Did not receive page filter data for filter key ${filterConfig.key}.`
    );
  }

  if (filterData.type === 'error') {
    throw new PermanentError(
      `An error occurred while retrieving filter data for filter key ${filterConfig.key}.`,
      filterData.cause
    );
  }

  if (activeDynamicNavigationItem?.href) {
    const dynamicItemUrlSearch = new URLSearchParams(
      activeDynamicNavigationItem?.href.split('?').pop()
    );
    const { detailsDiscriminator, detailsValue } =
      getDetailsFromSearchParams(dynamicItemUrlSearch);

    if (
      detailsDiscriminator === filterConfig.key &&
      filterData.selection == null
    ) {
      throw new Error(
        `An error occurred while retrieving data for detail page filter key ${filterConfig.key}. The filter value '${detailsValue}' is not a valid selection.`
      );
    }
  }

  if (
    filterConfig.display === 'multi-select' &&
    isMultiSelectFilter(filterData)
  ) {
    return { display: filterConfig.display, ...filterData };
  } else if (
    filterConfig.display === 'single-select' &&
    isSingleSelectFilter(filterData)
  ) {
    return { display: filterConfig.display, ...filterData };
  } else if (
    filterConfig.display === 'single-date-picker' &&
    filterData.type === 'date' &&
    isSingleSelectFilter(filterData)
  ) {
    return { display: filterConfig.display, ...filterData };
  } else if (
    isDateRangePickerDisplay(filterConfig.display) &&
    isDateRangeFilter(filterData)
  ) {
    return { display: filterConfig.display, ...filterData };
  } else if (
    filterConfig.display === 'range-number-picker' &&
    isNumberRangeFilter(filterData)
  ) {
    return { display: filterConfig.display, ...filterData };
  } else if (
    filterConfig.display === 'search' &&
    isSearchStringFilter(filterData)
  ) {
    return { display: filterConfig.display, ...filterData };
  } else if (
    filterConfig.display === 'comparison' &&
    isMultiSelectFilter(filterData) &&
    filterData.type !== 'date'
  ) {
    return { display: filterConfig.display, ...filterData };
  }
  throw new PermanentError(
    `Mapping of filter config to filter data failed for key ${filterConfig.key}`
  );
}

export function validateFiltersFromResponse(
  filterConfigs: FilterConfigDto[],
  data: DataFilterDefinitionsDtoResponse,
  activeDynamicNavigationItem?: NavigationCustomizationItemDto
): Record<string, FilterData> {
  return filterConfigs.reduce<Record<string, FilterData>>((acc, config) => {
    acc[config.key] = validateSingleFilterFromResponse(
      config,
      data.data[config.key],
      activeDynamicNavigationItem
    );
    return acc;
  }, {});
}
